import React, { useEffect, useState } from "react";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import { Controller, useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import MenuItem from "@mui/material/MenuItem";
import { FormControl, InputLabel, Select, Autocomplete } from "@mui/material";
import Grid from "@mui/material/Grid";
import { mdrMasterModel, mdrMasterSchema } from "../../../../models/binMaster";
import ConfiguratorRoot from "../../../../examples/Configurator/ConfiguratorRoot";
import MDBox from "../../../../components/MDBox";
import MDTypography from "../../../../components/MDTypography";
import MDInput from "../../../../components/MDInput";
import MDButton from "../../../../components/MDButton";
import useApi from "../../../../components/Utils/api";
import OutlinedInput from "@mui/material/OutlinedInput";

function MdrConfiguratorNew({ openConfigurator, setOpenConfigurator, master, getMasters }) {
  const [merchants, setMerchants] = useState([]);
  const [defaultMasters, setDefaultMasters] = useState([]);
  const paymentTypes = [
    { label: "Visa Domestic Credit", namePrefix: "visaDomesticCredit" },
    { label: "Visa Domestic Debit", namePrefix: "visaDomesticDebit" },
    { label: "Visa International", namePrefix: "visaInternational" },
    { label: "Mastercard Dom Credit", namePrefix: "mastercardDomesticCredit" },
    { label: "Mastercard Dom Debit", namePrefix: "mastercardDomesticDebit" },
    { label: "Mastercard International", namePrefix: "mastercardInternational" },
    { label: "eftpos Australia", namePrefix: "eftposAustralia" },
    { label: "Discover (Diners)", namePrefix: "discoverDiners" },
    { label: "Amex", namePrefix: "amex" },
    { label: "UPI", namePrefix: "upi" },
    { label: "JCB", namePrefix: "jcb" },
  ];
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    trigger,
    watch,
    control,
    setValue
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(mdrMasterSchema),
    defaultValues: {
      ...paymentTypes.reduce((acc, type) => ({
        ...acc,
        [`${type.namePrefix}_msf_rate`]: "0",
        [`${type.namePrefix}_msf_value`]: "0.00"
      }), {})
    }
  });

  const inputStyles = {
    '& .MuiInputBase-root': {
      height: '32px',
      fontSize: '0.875rem',
      padding: '0 8px',
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.875rem',
      lineHeight: '32px',
    },
    '& .MuiFormHelperText-root': {
      fontSize: '0.75rem',
    },
  };

  const settlementType = watch("settlementType");

  useEffect(() => {
    getAllReqInfoMaster();
    if (Object.keys(master).length > 0) {
      reset(master);
    } else {
      getDefaultMasters();
      reset(mdrMasterModel);
    }
  }, [master]);

  useEffect(() => {
    // Set default values for all payment types
    paymentTypes.forEach((type) => {
      setValue(`${type.namePrefix}_msf_rate`, "0");
      setValue(`${type.namePrefix}_msf_value`, "0.00");
    });
  }, []); // Empty dependency array means this runs once on mount

  useEffect(() => {
    if (settlementType === "NET") {
      // Set all mdrTypes to Blended when settlement type is NET
      paymentTypes.forEach((type) => {
        setValue(`${type.namePrefix}_mdrType`, "Blended");
      });
    }
  }, [settlementType, setValue]);

  const api = useApi();

  const getAllReqInfoMaster = () => {
    const data = { mid: "", ownerName: "", referenceId: "" };
    api.post("/merchant/browse", data)
      .then(response => {
        if (response.data.statusCodeValue === 200) {
          setMerchants(response.data.body);
        }
      })
      .catch(error => {
        // Handle error
      });
  };

  const getDefaultMasters = () => {
    api.post("master/getDefaultMdrMaster")
      .then(response => {
        if (response.data.statusCodeValue === 200) {
          setDefaultMasters(response.data.body[0]);
          reset(response.data.body[0]);
        }
      })
      .catch(error => {
        if (error.response && error.response.status === 403) {
          toast.error("You do not have permission to perform this action.");
          return;
        }
        toast.error(error.response.data.body);
      });
  };

  const createTerminalHandler = (data) => {
    api.post("master/createMdrMasternew", data)
      .then(response => {
        if (response.status === 200) {
          toast.success("MDR master successfully created.");
          setOpenConfigurator(false);
          getMasters();
        }
      })
      .catch(error => {
        if (error.response && error.response.status === 403) {
          toast.error("You do not have permission to perform this action.");
          return;
        }
        console.log(error.response);
        if (error.response.status === 406) {
          toast.error(error.response.data.body);
        }
      });
  };

  const updateTerminalHandler = (data) => {
    api.put(`master/mdrMaster/new/${master.mcId}`, data)
      .then(response => {
        if (response.data.statusCodeValue === 200) {
          toast.success("MDR master successfully updated.");
          reset(mdrMasterModel);
          setOpenConfigurator(false);
          getMasters();
        }
      })
      .catch(error => {
        console.log(error);
      });
  };

  const submitHandler = () => {
    if (Object.keys(master).length === 0) {
      handleSubmit(createTerminalHandler)();
    } else {
      handleSubmit(updateTerminalHandler)();
    }
  };

  return (
    <ConfiguratorRoot variant="permanent" ownerState={{ openConfigurator }} configuratorwidth={1000}>
      <MDBox display="flex" justifyContent="space-between" alignItems="baseline" pt={4} pb={0.5} px={3}>
        <MDBox>
        <MDTypography variant="h5">
  {Object.keys(master).length > 0 ? 'Update' : (master === null ? 'Browse' : 'New')} MDR Master
</MDTypography>        </MDBox>
        <Icon
          sx={({ typography: { size }, palette: { dark } }) => ({
            fontSize: `${size.lg} !important`,
            color: dark.main,
            stroke: "currentColor",
            strokeWidth: "2px",
            cursor: "pointer",
            transform: "translateY(5px)",
          })}
          onClick={() => setOpenConfigurator(false)}
        >
          close
        </Icon>
      </MDBox>
      <Divider />
      <Grid container spacing={2} justifyContent="center">
        <FormProvider {...{ register, handleSubmit, reset, formState: { errors }, trigger, watch, control }}>
          {/* Settlement Type */}
          <Grid item xs={6}>
            <FormControl fullWidth sx={inputStyles}>
              <InputLabel id="settlement-label">Settlement Type</InputLabel>
              <Controller
                name="settlementType"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select {...field} labelId="settlement-label" input={<OutlinedInput label="Settlement Type" />}>
                    <MenuItem value="">- Select  -</MenuItem>
                    <MenuItem value="GROSS"> Gross Settlement </MenuItem>
                    <MenuItem value="NET"> Net Settlement </MenuItem>
                  </Select>
                )}
              />
            </FormControl>
          </Grid>

          {/* Merchant */}
          <Grid item xs={6}>
            <Controller
              name="mid"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={merchants}
                  getOptionLabel={(option) => option ? `${option.mid} - ${option.companyName}` : ''}
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      label="Merchant"
                      error={errors.mid}
                      helperText={errors.mid ? errors.mid.message : ''}
                      fullWidth
                      sx={inputStyles}
                    />
                  )}
                  onChange={(_, newValue) => {
                    field.onChange(newValue ? newValue.mid : '');
                  }}
                  value={merchants.find(merchant => merchant.mid === field.value) || null}
                />
              )}
            />
          </Grid>

          {/* Dynamic Payment Types */}
          {paymentTypes.map((type) => (
            <React.Fragment key={type.namePrefix}>
              <Grid item xs={3}>
                <MDTypography
                  variant="body2"
                  sx={{
                    fontSize: '0.8rem',
                    fontWeight: 'bold',
                    border: '1px solid black',
                    padding: '4px',
                    borderRadius: '4px',
                  }}
                >
                  {type.label}
                </MDTypography>
              </Grid>
              <Grid item xs={3}>
                <FormControl fullWidth sx={inputStyles}>
                  <InputLabel id={`${type.namePrefix}_mdr_label`}></InputLabel>
                  <Controller
                    name={`${type.namePrefix}_mdrType`}
                    control={control}
                    defaultValue="Blended"
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId={`${type.namePrefix}_mdr_label`}
                        input={<OutlinedInput label="" />}
                        disabled={settlementType === "NET"}
                        value={settlementType === "NET" ? "Blended" : field.value}
                      >
                        <MenuItem value="---"></MenuItem>
                        <MenuItem value="Blended">Blended</MenuItem>
                        <MenuItem value="IC++">IC++</MenuItem>
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={3}>
              <Controller
                name={`${type.namePrefix}_msf_rate`}
                control={control}
                defaultValue="0"
                render={({ field }) => (
                  <MDInput
                    {...field}
                    defaultValue="0"
                    type="text"
                    label="MSF Rate %"
                    error={errors[`${type.namePrefix}_msf_rate`]}
                    helperText={errors[`${type.namePrefix}_msf_rate`] ? errors[`${type.namePrefix}_msf_rate`].message : ''}
                    fullWidth
                    sx={inputStyles}
                  />
                )}
              />
              </Grid>
              <Grid item xs={3}>
              <Controller
                name={`${type.namePrefix}_msf_value`}
                control={control}
                defaultValue="0.00"
                render={({ field }) => (
                  <MDInput
                    {...field}
                    defaultValue="0.00"
                    type="text"
                    label="Fee $"
                    error={errors[`${type.namePrefix}_msf_value`]}
                    helperText={errors[`${type.namePrefix}_msf_value`] ? errors[`${type.namePrefix}_msf_value`].message : ''}
                    fullWidth
                    sx={inputStyles}
                  />
                )}
              />
              </Grid>
              {/* <Grid item xs={2}>
                <MDInput
                  type="text"
                  label="Surcharge %"
                  defaultValue="0.00"
                  name={`${type.namePrefix}_surcharge_rate`}
                  error={errors[`${type.namePrefix}_surcharge_rate`]}
                  helperText={errors[`${type.namePrefix}_surcharge_rate`] ? errors[`${type.namePrefix}_surcharge_rate`].message : ''}
                  fullWidth
                  {...register(`${type.namePrefix}_surcharge_rate`)}
                  sx={inputStyles}
                />
              </Grid>
              <Grid item xs={2}>
                <MDInput
                  type="text"
                  label="Surcharge $"
                  defaultValue="0.00"
                  name={`${type.namePrefix}_surcharge_value`}
                  error={errors[`${type.namePrefix}_surcharge_value`]}
                  helperText={errors[`${type.namePrefix}_surcharge_value`] ? errors[`${type.namePrefix}_surcharge_value`].message : ''}
                  fullWidth
                  {...register(`${type.namePrefix}_surcharge_value`)}
                  sx={inputStyles}
                />
              </Grid> */}
            </React.Fragment>
          ))}

          {/* Submit Button */}
          <Grid item xs={12}>
            <MDButton onClick={() => submitHandler()} variant="gradient" color="info" style={{ float: 'right' }}>
              {Object.keys(master).length > 0 ? 'Update' : 'Add'}
            </MDButton>
          </Grid>
        </FormProvider>
      </Grid>
    </ConfiguratorRoot>
  );
}

MdrConfiguratorNew.defaultProps = {
  openConfigurator: false,
  setOpenConfigurator: () => {},
  getMasters: () => {},
  master: {},
};

MdrConfiguratorNew.propTypes = {
  openConfigurator: PropTypes.bool,
  setOpenConfigurator: PropTypes.func,
  getMasters: PropTypes.func,
  master: PropTypes.object,
};

export default MdrConfiguratorNew;
